import React from 'react'

import styled from 'styled-components'

import { Container } from 'src/components/partials/Container'
import { ResponsiveSectionWrapper } from 'src/components/partials/Section'
import { Breakpoints } from 'src/utilities/Breakpoints'

import { TopHat } from './Typography'

const LogostripSectionWrapper = styled(ResponsiveSectionWrapper)`
  img {
    display: block;
    width: 100%;
    margin: auto;
    height: auto;
  }
  .desktop {
    display: none;
  }
  .mobile {
    max-width: 600px;
  }
  @media (min-width: ${Breakpoints.sm}px) {
    .desktop {
      display: block;
    }
    .mobile {
      display: none;
    }
  }
`

const TextWrapper = styled.div`
  text-align: center;
  padding: 0 0 2.5rem;
`

export const LogostripSection = ({
  inverted,
  paddingLarge = '3rem 0',
  paddingSmall = '2rem 0',
  showTopHat = false,
  topHatText = 'Powering the world’s best data migration teams'
}: {
  inverted?: boolean
  paddingLarge?: string
  paddingSmall?: string
  showTopHat?: boolean
  topHatText?: string
}) => (
  <LogostripSectionWrapper
    paddingLarge={paddingLarge}
    paddingSmall={paddingSmall}
    backgroundColor={inverted ? 'black' : 'white'}
  >
    <Container>
      {showTopHat && (
        <TextWrapper>
          <TopHat color={inverted ? 'white' : 'black'}>{topHatText}</TopHat>
        </TextWrapper>
      )}

      {inverted ? (
        <>
          <img
            alt={
              'Logos of Flatfile customers: ADP, ServiceTitan, ' +
              'Square, ClickUp, AstraZeneca, Sage'
            }
            className='desktop logoStrip'
            height='38'
            loading='lazy'
            src={'/img/shared/logostrip-desktop-white-2.svg'}
            width='1050'
          />
          <img
            alt='Logos of Flatfile customers: Square, ClickUp, AstraZeneca, Sage'
            className='mobile logoStrip'
            height='36'
            loading='lazy'
            src={'/img/shared/customer-logos-sm-white.png'}
            width='650'
          />
        </>
      ) : (
        <>
          <img
            alt={
              'Logos of Flatfile customers: ADP, ServiceTitan, ' +
              'Square, ClickUp, AstraZeneca, Sage'
            }
            className='desktop logoStrip'
            height='38'
            loading='lazy'
            src={'/img/shared/logostrip-desktop-2.svg'}
            width='1050'
          />
          <img
            alt='Logos of Flatfile customers: Square, ClickUp, AstraZeneca, Sage'
            className='mobile logoStrip'
            height='38'
            loading='lazy'
            src={
              '//images.ctfassets.net/hjneo4qi4goj/7jMts3IBQikDBqUXSaPTxG/' +
              'ec31a83dc91f108989cee4c13326e872/Logo_Strip_C__Small___1_.svg'
            }
            width='325'
          />
        </>
      )}
    </Container>
  </LogostripSectionWrapper>
)

const LogoStripAltWrapper = styled.div<{ maxWidth?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  /* gap: 1rem; */
  flex-direction: row;
  flex-wrap: wrap;
  margin-inline: auto;
  max-width: ${props => props.maxWidth || '100%'};
`

export const LogostripAlt = ({
  backgroundColor,
  logos, // Added a new prop to receive an array of logo objects
  inverted,
  paddingLarge = '3rem 0 4rem',
  paddingSmall = '2rem 0 3rem',
  showTopHat = false,
  topHatText = 'Powering the world’s best data migration teams',
  topHatCase = 'uppercase',
  maxWidth = '100%'
}: {
  backgroundColor?: string
  logos?: { url: string; alt: string }[] // Each logo object has a URL and an alt text
  inverted?: boolean
  paddingLarge?: string
  paddingSmall?: string
  showTopHat?: boolean
  topHatText?: string
  topHatCase?: 'uppercase' | 'normal'
  maxWidth?: string
}) => (
  <LogostripSectionWrapper
    paddingLarge={paddingLarge}
    paddingSmall={paddingSmall}
    backgroundColor={backgroundColor || (inverted ? 'black' : 'white')}
  >
    <div className='!max-w-[76rem] mx-auto px-4'>
      {showTopHat && (
        <TextWrapper>
          <TopHat color={inverted ? 'white' : 'black'} case={topHatCase}>
            {topHatText}
          </TopHat>
        </TextWrapper>
      )}
      <LogoStripAltWrapper maxWidth={maxWidth} className='gap-4 lg:gap-8'>
        {logos &&
          logos.map((logo, index) => (
            <img
              key={index}
              alt={logo.alt}
              className={`logo ${inverted ? 'inverted' : ''}`} // Conditional class for styling
              src={logo.url}
              style={{
                height: 'auto',
                width: 'auto',
                maxHeight: '32px',
                padding: '0 10px'
              }} // Example styles, adjust as needed
            />
          ))}
      </LogoStripAltWrapper>
    </div>
  </LogostripSectionWrapper>
)

// Example usage:
// <LogostripAlt logos={[
//   { url: '/path/to/logo1.svg', alt: 'Logo 1' },
//   { url: '/path/to/logo2.png', alt: 'Logo 2' }
// ]} inverted={true} />

const LOGO_LIST = [
  { url: '/img/shared/logo-adp.svg', alt: 'ADP logo', name: 'adp' },
  {
    url: '/img/shared/logo-servicetitan.svg',
    alt: 'ServiceTitan logo',
    name: 'service titan'
  },
  { url: '/img/shared/logo-square.svg', alt: 'Square logo', name: 'square' },
  { url: '/img/shared/logo-clickup.svg', alt: 'ClickUp logo', name: 'clickup' },
  {
    url: '/img/shared/logo-astrazeneca.svg',
    alt: 'AstraZeneca logo',
    name: 'astrazeneca'
  },
  { url: '/img/shared/logo-sage.svg', alt: 'Sage logo', name: 'sage' }
]

export const LogoStrip = ({
  logoKeys, // New prop to specify which logos to display
  inverted,
  paddingLarge = '3rem 0 4rem',
  paddingSmall = '2rem 0 3rem',
  showTopHat = false,
  topHatText = "Powering the world's best data migration teams"
}: {
  logoKeys?: string[] // Array of keys (alt text) to select logos
  inverted?: boolean
  paddingLarge?: string
  paddingSmall?: string
  showTopHat?: boolean
  topHatText?: string
}) => {
  // Filter logos based on the provided keys
  const selectedLogos = logoKeys
    ? LOGO_LIST.filter(logo => logoKeys.includes(logo.name))
    : LOGO_LIST

  return (
    <LogostripSectionWrapper
      paddingLarge={paddingLarge}
      paddingSmall={paddingSmall}
      backgroundColor={inverted ? 'black' : 'white'}
    >
      <Container>
        {showTopHat && (
          <TextWrapper>
            <TopHat color={inverted ? 'white' : 'black'}>{topHatText}</TopHat>
          </TextWrapper>
        )}
        <LogoStripAltWrapper>
          {selectedLogos.map((logo, index) => (
            <img
              key={index}
              alt={logo.alt}
              className={`logo ${inverted ? 'inverted' : ''}`}
              src={logo.url}
              style={{
                height: 'auto',
                width: 'auto',
                maxHeight: '32px',
                padding: '0 10px'
              }}
            />
          ))}
        </LogoStripAltWrapper>
      </Container>
    </LogostripSectionWrapper>
  )
}

// Example usage:
// <LogoStrip
//   logoKeys={['ADP logo', 'Square logo', 'Sage logo']}
//   inverted={true}
// />
